import { defineStore } from 'pinia';
import { markRaw } from 'vue';

/** Types */
import type { KycCompliance } from '@/types/KycCompliance';
import type { AccountStateType, ParamsKycStatus } from './types';
import type { Account, AccountExecutiveData, Balance } from '@/types/AccountType';
import type { AxiosResponse } from 'axios';

/** Services */
import { accountClient, authClient } from '@/api';
import { accountService } from '@/shared/common/services/account.service';
import { authService } from '@/shared/common/services/auth.service';
import { syncUserProperties } from '@/shared/firebase';
import { companyService } from '@/shared/common';

import { logger } from '@/shared/common/utils/logger.util';

/** Helpers */
import {
  isCompanyKAM,
  isCompanyRejected,
  isCompanyIncomplete,
  isCompanyPending,
  isOnboardingCompleted,
  isCompanyFullyBlocked,
  isCompanyBlocked,
  isUserVerifiedPhone,
  isUserRejected,
  isUserIncomplete,
  isUserProcess,
  isUserComplete,
  isCompanyComplete,
  isKycWarningCompany,
  shouldShowSelfManagedOnboarding,
  isCustomerReview,
  isUserLegalRepresentative,
  isUserRequestedPartner,
} from '@/helpers/kyc';

export const useAccountStore = defineStore('account', {
  state: (): AccountStateType => ({
    wallets: [],
    kycStatus: {} as KycCompliance,
    lastConnectionDate: '',
    executive: {} as AccountExecutiveData,
    executiveDataFetched: false,
    executiveDataInFlight: null,
    companyInformation: null,
    companyInformationInFlight: null,
    strictBeneficiaryStatus: null,
    strictBeneficiaryStatusInFlight: null,
  }),
  getters: {
    getBalanceWalletMain(): Balance {
      if (this.wallets.length) {
        const walletMain = this.wallets.find((wallet) => wallet.clientMain);
        if (walletMain) {
          return walletMain.balance;
        }
      }
      return {
        availableBalance: '0',
        totalBalance: '0',
        lockedBalance: '',
        currency: '',
        currencySymbol: '',
      };
    },
    getWalletMain(): Account {
      if (this.wallets.length) {
        const walletMain = this.wallets.find((wallet) => wallet.clientMain);
        if (walletMain) {
          return walletMain;
        }
      }
      return {} as Account;
    },
    getLevel(): number {
      return this.kycStatus?.b2bUser?.level || 1;
    },
    getUserId(): number | undefined {
      return this.kycStatus?.b2bUser?.id;
    },
    getIsVerifiedPhone(): boolean {
      return this.kycStatus?.b2bUser?.verifiedPhone || false;
    },
    getCompanyCountry(): string {
      return this.kycStatus?.company?.addressCountry?.country || '';
    },
    isKycCompanyKAM(): boolean {
      return isCompanyKAM(this.kycStatus);
    },
    isKycCompanyRejected(): boolean {
      return isCompanyRejected(this.kycStatus);
    },
    isKycCompanyIncomplete(): boolean {
      return isCompanyIncomplete(this.kycStatus);
    },
    isKycCompanyPending(): boolean {
      return isCompanyPending(this.kycStatus);
    },
    isKycRejected(): boolean {
      return (
        isCompanyRejected(this.kycStatus) &&
        isUserRejected(this.kycStatus?.b2bUser)
      );
    },
    isKycCompleted(): boolean {
      return isOnboardingCompleted(this.kycStatus);
    },
    /**
     * Indica si el endpoint de KYC ya respondió y los datos del usuario
     * están disponibles para evaluar segmentación de feature flags. Se
     * apoya en `company.id` porque es el primer atributo que aparece en
     * la respuesta y, sin él, ninguna regla de segmentación puede
     * computarse de forma confiable.
     */
    isKycReady(): boolean {
      return Boolean(this.kycStatus?.company?.id);
    },
    isKycCompanyFullyBlocked(): boolean {
      return isCompanyFullyBlocked(this.kycStatus);
    },
    isKycCompanyBlocked(): boolean {
      return isCompanyBlocked(this.kycStatus);
    },
    isKycCompanyCompleted(): boolean {
      return isCompanyComplete(this.kycStatus?.company);
    },
    isKycWarningCompany(): boolean {
      return isKycWarningCompany(this.kycStatus);
    },
    shouldShowSelfManagedOnboarding(): boolean {
      return shouldShowSelfManagedOnboarding(this.kycStatus);
    },
    isKycUserVerifiedPhone(): boolean {
      return isUserVerifiedPhone(this.kycStatus?.b2bUser) || false;
    },
    isKycUserIncomplete(): boolean {
      return isUserIncomplete(this.kycStatus?.b2bUser);
    },
    isKycUserProcess(): boolean {
      return isUserProcess(this.kycStatus?.b2bUser);
    },
    isKycUserRejected(): boolean {
      return isUserRejected(this.kycStatus?.b2bUser);
    },
    isKycUserComplete(): boolean {
      return isUserComplete(this.kycStatus?.b2bUser);
    },
    isCustomerReview(): boolean {
      return isCustomerReview(this.kycStatus);
    },
    isKycUserLegalRepresentative(): boolean {
      return isUserLegalRepresentative(this.kycStatus);
    },
    isUserRequestedPartner(): boolean {
      return isUserRequestedPartner(this.kycStatus);
    },
  },
  actions: {
    /**
     * Carga las cuentas (wallets de Account) del usuario desde accountService y actualiza el state.
     * Absorbe el fold internamente. El retorno indica si la carga fue exitosa
     * para que los callers puedan diferenciar entre "sin cuentas" y "fallo de carga".
     * Los callers leen el state reactivo (accountStore.wallets) con storeToRefs.
     * @returns Promise<boolean> — true si la carga fue exitosa, false si falló.
     */
    async setAccount(): Promise<boolean> {
      const result = await accountService.getAccounts();
      let succeeded = false;
      result.fold(
        (error) => {
          logger.warn('Failed to load account wallets', {
            code: error.code,
            status: error.status,
            reason: error.reason,
          });
        },
        (success) => {
          succeeded = true;
          this.wallets = success.data;
        }
      );
      return succeeded;
    },
    setKyc(payload: KycCompliance): void {
      this.kycStatus = payload;
      this.strictBeneficiaryStatus = null;
      this.strictBeneficiaryStatusInFlight = null;
      localStorage.setItem(
        'g-user',
        JSON.stringify({
          name: payload.b2bUser.name,
          lastName: payload.b2bUser.lastName,
          email: payload.b2bUser.email,
          company: payload.company.name,
        })
      );

      syncUserProperties({
        b2bUserId: payload.b2bUser?.id?.toString() ?? 'unknown',
        companyId: payload.company?.id?.toString() ?? 'unknown',
        companyCreatedAt: payload.company?.createAt,
        originCountry: payload.company?.addressCountry?.country ?? 'unknown',
        kycStatus: payload.company?.kycStage1,
        complianceStatus: payload.company?.complianceStatus,
      });
    },
    async setKycStatus(
      params?: ParamsKycStatus
    ): Promise<AxiosResponse<KycCompliance>> {
      if (this.kycStatus?.b2bUser?.id && !params?.ignoreCache) {
        return Promise.resolve({
          data: this.kycStatus,
        } as AxiosResponse<KycCompliance>);
      }

      const res = await authClient.getKycStatus();
      this.setKyc(res.data);
      return res;
    },
    /**
     * Consulta si la empresa tiene beneficiarios finales aprobados, ignorando el
     * documento generado que la versión por defecto del endpoint acepta como
     * equivalente. Cachea el resultado hasta el siguiente `setKyc`.
     * @returns Promesa con el estado consultado, o null si la consulta falló.
     */
    async loadStrictBeneficiaryStatus(): Promise<boolean | null> {
      if (this.strictBeneficiaryStatus !== null) return this.strictBeneficiaryStatus;
      if (this.strictBeneficiaryStatusInFlight) {
        return this.strictBeneficiaryStatusInFlight;
      }

      const request = (async (): Promise<boolean | null> => {
        const result = await authService.getKycStatusStrictBeneficiary();

        return result.fold(
          (failure) => {
            logger.warn('Error al consultar beneficiarios finales aprobados', {
              failure,
            });
            return null;
          },
          (success) => {
            this.strictBeneficiaryStatus = Boolean(
              success.data?.company?.hasBeneficiaryFile
            );
            return this.strictBeneficiaryStatus;
          }
        );
      })().finally(() => {
        this.strictBeneficiaryStatusInFlight = null;
      });

      this.strictBeneficiaryStatusInFlight = markRaw(request);
      return request;
    },
    async setExecutiveData(): Promise<void> {
      if (this.executiveDataFetched) return;
      if (this.executiveDataInFlight) return this.executiveDataInFlight;

      const request = (async (): Promise<void> => {
        try {
          const res = await accountClient.getAccountExecutiveData();
          this.executive = res.data;
        } catch (error) {
          logger.error('Error al obtener el ejecutivo de cuenta', {}, error instanceof Error ? error : undefined);
        } finally {
          this.executiveDataFetched = true;
          this.executiveDataInFlight = null;
        }
      })();

      this.executiveDataInFlight = markRaw(request);
      return request;
    },
    async setCompanyInformation(force = false): Promise<void> {
      if (this.companyInformation && !force) return;
      if (!force && this.companyInformationInFlight) return this.companyInformationInFlight;

      const request = (async (): Promise<void> => {
        try {
          const result = await companyService.getCompanyInformation();
          result.fold(
            () => {
              this.companyInformation = null;
            },
            (success) => {
              this.companyInformation = success.data;
            }
          );
        } finally {
          this.companyInformationInFlight = null;
        }
      })();

      this.companyInformationInFlight = markRaw(request);
      return request;
    },
  },
});
